/**
 * The environment a /code agent suggests with its `suggest_environment` tool,
 * as the transcript shows it for the user to review and save, and the check
 * that runs the saved script in a fresh sandbox. Devin works the same way: its
 * agent proposes the setup as cards in the timeline, and a build from the
 * approved setup shows whether it works. No Node imports: the transcript
 * card uses it in the browser.
 */

import { z } from 'zod';

import type { CodeRepositoryEnvironmentView } from './types';

import {
  ASSIGNMENT_START_PATTERN,
  parseDotenvText,
  quoteIndex,
  VALUE_QUOTES,
} from './dotenv-syntax';
import { isOutboundHostAllowed, isOutboundHostName, normalizeOutboundHost } from './outbound-hosts';

/** The OpenCode tool the agent calls with its suggestion; the call's input is the suggestion. */
export const ENVIRONMENT_SUGGESTION_TOOL = 'suggest_environment';

export const environmentSuggestionSchema = z.object({
  setupScript: z.string(),
  /** Blank, saving keeps the saved start instructions; given, it replaces them. */
  startInstructions: z.string().default(''),
  variables: z.array(z.object({ name: z.string().min(1), value: z.string() })).default([]),
  /** Variables whose real value is a secret on the user's machine, which the agent never guesses. */
  secrets: z
    .array(
      z.object({
        name: z.string().min(1),
        reason: z.string(),
        sourceHint: z.string().optional(),
      }),
    )
    .default([]),
  credentials: z
    .array(
      z.object({
        host: z.string().min(1),
        reason: z.string(),
        /** What the value is called on the user's machine, such as NPM_TOKEN. */
        name: z.string().optional(),
        headerName: z.string().optional(),
        headerPrefix: z.string().optional(),
        sourceHint: z.string().optional(),
        /** For npm.pkg.github.com: the npm scopes the repository installs from it, such as lucis-team. */
        packageScopes: z.array(z.string()).optional(),
      }),
    )
    .default([]),
  /**
   * Hosts restricted internet access blocks that the script or the repository's
   * usual commands need. Saving the suggestion never allows them: an
   * installation admin does, from the card.
   */
  allowedHosts: z.array(z.object({ host: z.string().min(1), reason: z.string() })).default([]),
});

export type EnvironmentSuggestion = z.infer<typeof environmentSuggestionSchema>;

/** Unix line endings, which bash needs, from Windows and old Mac ones alike. */
export function toUnixLineEndings(text: string): string {
  return text.replace(/\r\n?/g, '\n');
}

/** The suggestion in a tool call's input, or null when the input is not one. */
export function parseEnvironmentSuggestion(input: unknown): EnvironmentSuggestion | null {
  const parsed = environmentSuggestionSchema.safeParse(input);
  return parsed.success ? parsed.data : null;
}

/**
 * The variables saving `suggestion` sets: each one it does not also list as a
 * secret, or the environment saved as a secret, whose value comes from the
 * user's machine, such as by a secrets upload, and must not be replaced by the agent's.
 */
export function suggestedVariables(
  suggestion: Pick<EnvironmentSuggestion, 'variables' | 'secrets'>,
  savedSecretNames: readonly string[] = [],
): EnvironmentSuggestion['variables'] {
  const secrets = new Set([
    ...suggestion.secrets.map((secret) => secret.name),
    ...savedSecretNames,
  ]);
  return suggestion.variables.filter((variable) => !secrets.has(variable.name));
}

/** The most hosts one suggestion asks for: each one an admin allows can receive data from every session. */
export const SUGGESTED_HOSTS_MAX = 20;

type SuggestionEnvironment = Pick<
  CodeRepositoryEnvironmentView,
  'networkMode' | 'allowPackageRegistries' | 'allowedOutboundHosts' | 'credentials'
>;

/** A host a suggestion asks restricted internet access to allow, as its card lists it. */
export type SuggestedHost = { host: string; reason: string; allowed: boolean };

/**
 * The hosts a suggestion asks the repository to allow, trimmed and
 * lowercased, each once with its first reason, and marked when the
 * repository allows it already. A host no save would take is left out, as
 * is every host past the first `SUGGESTED_HOSTS_MAX`, and every host under
 * open access, where there is nothing to allow.
 */
export function suggestedHostsOf(
  suggestion: Pick<EnvironmentSuggestion, 'allowedHosts'>,
  environment: SuggestionEnvironment,
): SuggestedHost[] {
  if (environment.networkMode !== 'restricted') {
    return [];
  }
  const hosts = new Map<string, SuggestedHost>();
  for (const { host, reason } of suggestion.allowedHosts) {
    if (hosts.size === SUGGESTED_HOSTS_MAX) {
      break;
    }
    const name = normalizeOutboundHost(host);
    if (isOutboundHostName(name) && !hosts.has(name)) {
      hosts.set(name, { host: name, reason, allowed: isOutboundHostAllowed(name, environment) });
    }
  }
  return [...hosts.values()];
}

/**
 * Whether a suggestion asks for a host the repository does not allow yet. Its
 * script's check would fail to reach the host, so the card and the first
 * environment's save hold the check until an installation admin allows it.
 */
export function hostsPending(
  suggestion: Pick<EnvironmentSuggestion, 'allowedHosts'>,
  environment: SuggestionEnvironment,
): boolean {
  return suggestedHostsOf(suggestion, environment).some((host) => !host.allowed);
}

/**
 * What the server writes in place of a secret's value when it redacts text
 * (`redactText` in `coding-agent-controller/sanitize.ts`), as the transcript
 * shows a session's own secrets. The environment's MCP tools write it where a
 * text copies a secret's value, and refuse to save it back.
 */
export const REDACTED_VALUE = '[REDACTED]';

/** The environment's script, start instructions and `.env` variables text. */
export type CodeEnvironmentFields = {
  setupScript: string;
  startInstructions: string;
  environmentVariables: string;
};

/** The saved fields, and the names of variables saved as secrets and of those someone left out. */
export type SuggestionTarget = CodeEnvironmentFields & {
  secretVariableNames: readonly string[];
  leftOutVariableNames: readonly string[];
};

type Applied = { ok: true; value: CodeEnvironmentFields } | { ok: false; error: string };

/** A value dotenv reads back unchanged without quotes; a backslash stays literal there. */
const BARE_VALUE_PATTERN = /^[\w@%+=:,./\\-]*$/;

/**
 * `NAME=value` as dotenv reads it back, and as the save's own check
 * (`parseEnvironmentVariables`) accepts it: bare when it can be, else in the
 * first quote dotenv keeps literal and the value lacks. Double quotes come
 * last, since dotenv expands `\n` and `\r` inside them. No quote holds a value
 * ending in a backslash, which the check reads as escaping the closing quote.
 * Null when no form fits.
 */
export function formatEnvironmentVariable(name: string, value: string): string | null {
  if (BARE_VALUE_PATTERN.test(value)) {
    return `${name}=${value}`;
  }
  if (value.endsWith('\\')) {
    return null;
  }
  for (const quote of ["'", '`']) {
    if (!value.includes(quote)) {
      return `${name}=${quote}${value}${quote}`;
    }
  }
  if (!value.includes('"') && !/\\[nr]/.test(value)) {
    return `${name}="${value}"`;
  }
  return null;
}

/**
 * The lines an assignment spans, from `start`: one, or up to the line that
 * closes a quoted value spanning lines.
 */
function assignmentLineCount(lines: readonly string[], start: number, matchLength: number) {
  const value = lines[start]!.slice(matchLength).trimStart();
  const quote = value[0] ?? '';
  if (!VALUE_QUOTES.has(quote) || quoteIndex(value, quote, 1) !== -1) {
    return 1;
  }
  for (let index = start + 1; index < lines.length; index += 1) {
    if (quoteIndex(lines[index]!, quote) !== -1) {
      return index - start + 1;
    }
  }
  return 1;
}

/**
 * Where `name` is assigned, as the lines it spans, or null. A line inside
 * another variable's multi-line value is part of that value, even when it
 * reads like an assignment.
 */
function findAssignment(lines: readonly string[], name: string) {
  let index = 0;
  while (index < lines.length) {
    const match = ASSIGNMENT_START_PATTERN.exec(lines[index]!);
    if (!match) {
      index += 1;
      continue;
    }
    const count = assignmentLineCount(lines, index, match[0].length);
    if (match[1] === name) {
      return { start: index, count };
    }
    index += count;
  }
  return null;
}

/**
 * The environment after saving `suggestion` over `current`: the script and
 * start instructions it gives, and each suggested variable set in place of an
 * existing assignment of its name, or added at the end. A blank script or
 * start instructions keeps the saved one, so a review page can save its own
 * part alone.
 * Variables the suggestion leaves out, also lists as secrets, or `current` saved as secrets, stay,
 * and so does one someone left out (`leftOutVariableNames`), whatever it holds.
 * A suggestion shown with a secret hidden in it cannot be saved: the hidden
 * value would be saved as `[REDACTED]`. Names and the result are validated by
 * the save, not here.
 */
export function applyEnvironmentSuggestion(
  current: SuggestionTarget,
  suggestion: EnvironmentSuggestion,
): Applied {
  if (JSON.stringify(suggestion).includes(REDACTED_VALUE)) {
    return { ok: false, error: 'Includes a hidden value' };
  }
  const leftOut = new Set(current.leftOutVariableNames);
  const environmentVariables = setEnvironmentVariables(
    current.environmentVariables,
    suggestedVariables(suggestion, current.secretVariableNames).filter(
      ({ name }) => !leftOut.has(name),
    ),
  );
  if (!environmentVariables.ok) {
    return environmentVariables;
  }
  return {
    ok: true,
    value: {
      setupScript: storedSuggestionText(suggestion.setupScript, current.setupScript),
      startInstructions: storedSuggestionText(
        suggestion.startInstructions,
        current.startInstructions,
      ),
      environmentVariables: environmentVariables.value,
    },
  };
}

/**
 * A suggested script or start instructions as saving it stores it, over the
 * saved one. Normalized here, so a saved suggestion reads as saved. Models
 * often send an empty string for a field they mean to leave out, and a review
 * page saves its own part with the others blank, so a blank one keeps the
 * saved one.
 */
export function storedSuggestionText(suggested: string, saved: string): string {
  return suggested.trim() ? toUnixLineEndings(suggested) : saved;
}

/** A suggested variable by name, with the value the suggestion sets, if it sets one. */
export type LeftOutVariable = { name: string; value?: string };

/**
 * The variables and left-out names after leaving `variables` out of
 * `current`, as a review's Leave out does, or adding them back. Left out, a
 * variable loses the value the suggestion sets when it holds exactly that one
 * (`unsetEnvironmentVariables`), and its name joins the list once; a value
 * someone set themselves stays. Added back, its name only leaves the list,
 * for the next save to set it.
 */
export function leaveOutVariables(
  current: Pick<SuggestionTarget, 'environmentVariables' | 'leftOutVariableNames'>,
  variables: readonly LeftOutVariable[],
  leftOut: boolean,
): { environmentVariables: string; leftOutVariableNames: string[] } {
  const named = new Set(variables.map(({ name }) => name));
  const others = current.leftOutVariableNames.filter((listed) => !named.has(listed));
  if (!leftOut) {
    return { environmentVariables: current.environmentVariables, leftOutVariableNames: others };
  }
  return {
    environmentVariables: unsetEnvironmentVariables(
      current.environmentVariables,
      variables.flatMap(({ name, value }) => (value === undefined ? [] : [{ name, value }])),
    ),
    leftOutVariableNames: [...others, ...named],
  };
}

/** Whether `.env` text assigns `name` a non-empty value. */
export function hasEnvironmentVariable(text: string, name: string): boolean {
  const lines = text.split('\n');
  const assignment = findAssignment(lines, name);
  if (!assignment) {
    return false;
  }
  const match = ASSIGNMENT_START_PATTERN.exec(lines[assignment.start]!);
  const value = lines[assignment.start]!.slice(match![0].length).trim();
  const quote = value[0] ?? '';
  // A quoted value is empty only when its closing quote comes right after the opening one.
  return VALUE_QUOTES.has(quote)
    ? quoteIndex(value, quote, 1) !== 1
    : value.split('#', 1)[0]!.trim() !== '';
}

/**
 * `.env` text with each variable set in place of an existing assignment of
 * its name, or added at the end. Variables left out stay. The error names the
 * variable, never its value. A variable dotenv would not read back as set is
 * refused, such as one a line before it runs on into.
 */
export function setEnvironmentVariables(
  text: string,
  variables: readonly { name: string; value: string }[],
): { ok: true; value: string } | { ok: false; error: string } {
  const lines = text === '' ? [] : text.split('\n');
  const added: string[] = [];
  for (const { name, value } of variables) {
    const line = formatEnvironmentVariable(name, value);
    if (line === null) {
      return { ok: false, error: `${name} can't be written as a .env value` };
    }
    const assignment = findAssignment(lines, name);
    if (!assignment) {
      added.push(line);
      continue;
    }
    lines.splice(assignment.start, assignment.count, line);
  }
  if (added.length > 0) {
    // Keep a final newline the text had, and start the added lines on their own line.
    const endsWithNewline = lines.at(-1) === '';
    if (endsWithNewline) {
      lines.pop();
    }
    lines.push(...added);
    if (endsWithNewline) {
      lines.push('');
    }
  }
  const written = lines.join('\n');
  const read = new Map(parseDotenvText(written).map(({ name, value }) => [name, value]));
  // dotenv makes each line break `\n`, in a value too. A name listed twice ends with its last value.
  const expected = new Map(variables.map(({ name, value }) => [name, toUnixLineEndings(value)]));
  for (const [name, value] of expected) {
    if (read.get(name) !== value) {
      return {
        ok: false,
        error: `${name} can't be set: the saved variables would read another value`,
      };
    }
  }
  return { ok: true, value: written };
}

/**
 * `.env` text without each variable's assignment where it holds exactly the
 * variable's value, as `setEnvironmentVariables` writes it, including an
 * empty one and every line it spans. An assignment of any other value stays,
 * as do the other lines and the final newline.
 */
export function unsetEnvironmentVariables(
  text: string,
  variables: readonly { name: string; value: string }[],
): string {
  const lines = text.split('\n');
  for (const { name, value } of variables) {
    const assignment = findAssignment(lines, name);
    const written = formatEnvironmentVariable(name, value);
    if (
      assignment &&
      written !== null &&
      lines.slice(assignment.start, assignment.start + assignment.count).join('\n') === written
    ) {
      lines.splice(assignment.start, assignment.count);
    }
  }
  return lines.join('\n');
}

/** `.env` text without the assignment of `name` and every line it spans, or null when none assigns it. */
export function removeEnvironmentVariable(text: string, name: string): string | null {
  const lines = text.split('\n');
  const assignment = findAssignment(lines, name);
  if (!assignment) {
    return null;
  }
  lines.splice(assignment.start, assignment.count);
  return lines.join('\n');
}

/** Whether saving a suggestion would change the environment, and why it cannot be saved, if it cannot. */
export type SuggestionSaveState = { unsaved: boolean; error: string | null };

/**
 * Whether saving `suggestion` would leave `current` as it is: it is saved
 * already. One that cannot be saved, such as one with a hidden value, says why.
 */
export function suggestionSaveState(
  current: SuggestionTarget,
  suggestion: EnvironmentSuggestion,
): SuggestionSaveState {
  const applied = applyEnvironmentSuggestion(current, suggestion);
  if (!applied.ok) {
    return { unsaved: true, error: applied.error };
  }
  return {
    unsaved:
      applied.value.setupScript !== current.setupScript ||
      applied.value.startInstructions !== current.startInstructions ||
      applied.value.environmentVariables !== current.environmentVariables,
    error: null,
  };
}

/**
 * A check of the saved environment, started from a suggestion's card: the
 * setup script run in a fresh sandbox on the session's base branch, as the
 * next session will run it.
 */
export const CODE_ENVIRONMENT_CHECK_STATUS = {
  RUNNING: 'running',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  /** The check could not run, such as when no VM could be prepared. Says nothing about the script. */
  UNAVAILABLE: 'unavailable',
} as const;

export const codeEnvironmentCheckSchema = z.object({
  /** Each start gets its own; only the latest start's result is kept. */
  checkId: z.string(),
  status: z.nativeEnum(CODE_ENVIRONMENT_CHECK_STATUS),
  /** How a failed script failed, as the setup row words it, such as "Failed with exit code 1". */
  detail: z.string().optional(),
  /** The end of a failed run's output, redacted. */
  output: z.array(z.string()).optional(),
  /** Epoch ms. */
  startedAt: z.number(),
  finishedAt: z.number().optional(),
});

export type CodeEnvironmentCheck = z.infer<typeof codeEnvironmentCheckSchema>;

/** `POST` starts a check (body `{ callId }`); `GET ?callId=` reads the latest. Both answer `{ check }`. */
export function codeEnvironmentCheckUrl(sessionId: string) {
  return `/api/code-sessions/${sessionId}/environment-check`;
}
